import React, { useId } from 'react';
import { Check, Play, RefreshCw } from 'lucide-react';
import type { SpotlightTone } from '../../data/whats-new';

const LOOP_ROUTES = ['Delhi NCR Office Run', 'Weekend Trip', 'Daily Routine'];

function DriveMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#34A853" d="M8.2 20.2 3.2 12h6.2l4.8 8.2z" />
      <path fill="#FBBC04" d="M15.6 20.2 10.6 12h9.8l-4.8 8.2z" />
      <path fill="#4285F4" d="M12 12.2 8.2 4.2h7.6L12 12.2z" />
    </svg>
  );
}

function Pin({ x, y, fill, label }: { x: number; y: number; fill: string; label: string }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r="13" fill={fill} />
      <circle r="13" fill="none" stroke="#fff" strokeWidth="2" />
      <text
        textAnchor="middle"
        y="4"
        fill="#fff"
        fontSize="calc(12px * var(--ml-type-descriptive))"
        fontWeight="700"
        fontFamily="var(--ml-font-family, Inter, sans-serif)"
      >
        {label}
      </text>
    </g>
  );
}

export function ReleaseIllustration({ cards = true }: { cards?: boolean }) {
  const raw = useId().replace(/:/g, '');
  const grid = `${raw}-grid`;
  return (
    <div
      className="wn-illus"
      data-testid={cards ? 'illustration-placeholder' : undefined}
      data-illustration="placeholder"
      aria-hidden="true"
    >
      <svg className="wn-illus__map" viewBox="0 0 560 340" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id={grid} width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M32 0H0V32" fill="none" stroke="#d7e6f4" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="560" height="340" fill="#e7f3fb" />
        <rect width="560" height="340" fill={`url(#${grid})`} />
        <ellipse cx="78" cy="52" rx="18" ry="18" fill="var(--ml-accent-soft, #ffe08a)" />
        <ellipse cx="168" cy="40" rx="26" ry="12" fill="#fff" opacity="0.85" />
        <ellipse cx="196" cy="36" rx="18" ry="10" fill="#fff" opacity="0.9" />
        <ellipse cx="120" cy="78" rx="46" ry="22" fill="#d9f3e2" />
        <ellipse cx="430" cy="250" rx="70" ry="28" fill="#d7f0e4" />
        <path
          d="M0 292c70-28 120 16 190-8 60-20 90 18 160-6 50-16 80 10 210-12v74H0z"
          fill="#d4eef8"
        />
        <path d="M20 168h150M250 40v90M400 120h140M80 230h90" fill="none" stroke="#c5d7e8" strokeWidth="7" strokeLinecap="round" />
        <rect x="250" y="200" width="22" height="16" rx="2" fill="#f8fbff" stroke="#d5e2ee" />
        <rect x="300" y="60" width="18" height="14" rx="2" fill="#f8fbff" stroke="#d5e2ee" />
        <path
          d="M70 250C120 246 150 190 198 156c36-26 20-70 78-62 48 6 40 62 92 74 42 10 70-20 112-48"
          fill="none"
          stroke="#93c5fd"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray="2 9"
        />
        <path
          d="M78 236C132 228 156 176 206 146c40-24 18-62 74-56 52 6 46 70 96 80 40 8 62-16 108-46"
          fill="none"
          stroke="var(--ml-info, #2f6bff)"
          strokeWidth="7"
          strokeLinecap="round"
        />
        <Pin x={118} y={214} fill="var(--ml-info, #3b82f6)" label="1" />
        <Pin x={236} y={118} fill="var(--ml-primary, #7c5cff)" label="2" />
        <Pin x={392} y={188} fill="var(--ml-danger, #ef4444)" label="3" />
        <Pin x={486} y={102} fill="var(--ml-warning, #f59e0b)" label="4" />
      </svg>
      {cards ? (
        <>
          <div className="wn-illus__badge">
            <RefreshCw size={26} />
          </div>
          <div className="wn-illus__loop">
            <header>
              <span>
                <RefreshCw size={14} />
                Loop Playlist
              </span>
              <span className="wn-illus__toggle" />
            </header>
            <ul className="wn-illus__routes">
              {LOOP_ROUTES.map((name) => (
                <li key={name}>
                  <span className="wn-illus__play">
                    <Play size={8} fill="currentColor" />
                  </span>
                  <span>{name}</span>
                  <span className="wn-illus__grip">⋮</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="wn-illus__drive">
            <DriveMark />
            <span>
              <strong>Google Drive Backup</strong>
              <small>
                <Check size={12} />
                Backed up 2 hours ago
              </small>
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function EmptyIllustration() {
  return (
    <svg className="wn-empty__art" data-testid="empty-illustration" viewBox="0 0 320 170" aria-hidden="true">
      <rect width="320" height="170" rx="24" fill="#e7f3fb" />
      <circle cx="262" cy="36" r="14" fill="var(--ml-accent-soft, #ffe08a)" />
      <path d="M0 118c48-26 78 10 128-8 42-14 64 18 112-2 28-12 48 8 80-10v72H0z" fill="#d5f0df" />
      <path
        d="M40 124c42-34 72-18 108-42 22-14 40-8 70 8"
        fill="none"
        stroke="var(--ml-info, #2f6bff)"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <circle cx="52" cy="120" r="8" fill="var(--ml-info, #3b82f6)" />
      <circle cx="156" cy="80" r="8" fill="var(--ml-primary, #7c5cff)" />
      <circle cx="236" cy="92" r="8" fill="var(--ml-danger, #ef4444)" />
      <circle cx="168" cy="112" r="18" fill="#fff" />
      <path d="M160 112a10 10 0 1 1 3 8" fill="none" stroke="var(--ml-info, #2f6bff)" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

export function HighlightArt({ tone }: { tone: SpotlightTone }) {
  if (tone === 'playlist') {
    return (
      <svg className="wn-art" viewBox="0 0 120 100" aria-hidden="true">
        <rect x="8" y="16" width="104" height="72" rx="18" fill="var(--ml-primary-wash, #efeaff)" />
        <path
          d="M24 70c16-26 28-10 44-26 12-12 20-8 30 6"
          fill="none"
          stroke="var(--ml-primary, #7c5cff)"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <circle cx="28" cy="68" r="7" fill="var(--ml-info, #3b82f6)" />
        <circle cx="64" cy="46" r="7" fill="var(--ml-primary-soft, #a78bfa)" />
        <circle cx="96" cy="52" r="7" fill="#f97316" />
        <circle cx="78" cy="74" r="12" fill="var(--ml-primary, #7c5cff)" />
        <polygon points="74,69 83,74 74,79" fill="#fff" />
      </svg>
    );
  }
  if (tone === 'backup') {
    return (
      <svg className="wn-art" viewBox="0 0 120 100" aria-hidden="true">
        <ellipse cx="60" cy="82" rx="36" ry="8" fill="#dbeafe" />
        <path
          d="M32 64h56a16 16 0 0 0-6-31 22 22 0 0 0-40-4 16 16 0 0 0-10 35z"
          fill="#fff"
          stroke="var(--ml-info, #60a5fa)"
          strokeWidth="2"
        />
        <path fill="#34A853" d="M50 62 44 50h8z" />
        <path fill="#FBBC04" d="M66 62 58 50h16z" />
        <path fill="#4285F4" d="M58 50 54 42h10z" />
        <circle cx="24" cy="78" r="7" fill="#93c5fd" />
        <circle cx="98" cy="74" r="8" fill="#bfdbfe" />
      </svg>
    );
  }
  if (tone === 'coins') {
    return (
      <svg className="wn-art" viewBox="0 0 120 100" aria-hidden="true">
        <ellipse cx="46" cy="72" rx="24" ry="9" fill="#e8b931" />
        <ellipse cx="46" cy="64" rx="24" ry="9" fill="var(--ml-accent-soft, #ffe08a)" />
        <ellipse cx="74" cy="56" rx="24" ry="9" fill="#f5c542" />
        <ellipse cx="74" cy="48" rx="24" ry="9" fill="#ffe9a8" />
        <text x="74" y="52" textAnchor="middle" fontSize="calc(12px * var(--ml-type-descriptive))" fontWeight="700" fill="var(--ml-warning, #b45309)" fontFamily="var(--ml-font-family, Inter, sans-serif)">
          M
        </text>
        <circle cx="98" cy="24" r="3" fill="var(--ml-warning, #fbbf24)" />
        <circle cx="28" cy="30" r="2.5" fill="#fde68a" />
        <path d="M90 34l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" fill="var(--ml-warning, #fbbf24)" />
      </svg>
    );
  }
  return (
    <svg className="wn-art" viewBox="0 0 120 100" aria-hidden="true">
      <rect x="16" y="16" width="88" height="68" rx="18" fill="var(--ml-primary-wash, #efeaff)" />
      <path d="M60 30l4 12 12 4-12 4-4 12-4-12-12-4 12-4z" fill="var(--ml-primary, #7c5cff)" />
      <circle cx="90" cy="32" r="4" fill="var(--ml-warning, #fbbf24)" />
      <circle cx="34" cy="70" r="3" fill="#fff" />
    </svg>
  );
}
